import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { ChevronDown, ExternalLink, MessageSquareText } from "lucide-react";
import { cn } from "@/lib/utils";
import { StarRating } from "./StarRating";

const TRUNCATE_AT = 150;

/* Shared read-more text block (also used by the negative reviews panel). */
export const ReviewText = ({ text, testId }) => {
  const [expanded, setExpanded] = useState(false);
  const long = (text || "").length > TRUNCATE_AT;
  const shown = expanded || !long ? text : `${text.slice(0, TRUNCATE_AT).trimEnd()}\u2026`;
  return (
    <p className="text-xs text-[hsl(var(--cx-ink))] leading-relaxed mt-1" data-testid={testId}>
      {shown}
      {long && (
        <button
          className="ml-1 text-[0.68rem] font-medium text-[hsl(var(--cx-navy))] underline underline-offset-2 hover:opacity-80"
          onClick={(e) => { e.stopPropagation(); setExpanded((v) => !v); }}
          data-testid={testId ? `${testId}-toggle` : undefined}>
          {expanded ? "Show less" : "Read more"}
        </button>
      )}
    </p>
  );
};

export const ReviewItem = ({ review, testId, showLocation }) => (
  <div
    className={cn("px-3 py-2 rounded-md border bg-[hsl(var(--card))]",
      review.negative || review.rating <= 2
        ? "border-l-2 border-l-[hsl(var(--cx-crimson))] border-[hsl(var(--cx-crimson)/0.25)] bg-[hsl(var(--cx-crimson)/0.03)]"
        : "border-[hsl(var(--border))]")}
    data-testid={testId}>
    <div className="flex items-center gap-1.5 flex-wrap">
      <span className="tabular text-xs font-semibold">{review.rating}</span>
      <StarRating value={review.rating} size={11} />
      {(review.negative || review.rating <= 2) && (
        <Badge className="text-[0.58rem] px-1.5 py-0 h-4 bg-[hsl(var(--cx-crimson))] text-white border-transparent hover:bg-[hsl(var(--cx-crimson))]">
          Negative
        </Badge>
      )}
      <span className="ml-auto text-[0.62rem] text-muted-foreground tabular whitespace-nowrap">
        {review.publish_date_fmt || review.first_seen}
      </span>
    </div>
    <p className="text-[0.68rem] text-muted-foreground mt-0.5">
      {review.author_display}
      {showLocation && review.outlet_name && (
        <> · {review.outlet_name} <span className="uppercase">{review.function}</span></>
      )}
    </p>
    <ReviewText text={review.text} testId={testId ? `${testId}-text` : undefined} />
  </div>
);

/* "Latest Google Reviews" block inside each rating card — negatives pinned. */
export const LatestReviews = ({ reviews, googleUrl, testBase }) => {
  const list = reviews || [];
  const negatives = list.filter((r) => r.negative || r.rating <= 2).length;
  const [open, setOpen] = useState(negatives > 0);
  if (list.length === 0) return null;
  return (
    <Collapsible open={open} onOpenChange={setOpen} className="mt-2.5 border-t pt-2">
      <CollapsibleTrigger asChild>
        <button
          className="w-full flex items-center gap-1.5 text-xs font-medium text-[hsl(var(--cx-ink))] hover:text-[hsl(var(--cx-navy))] transition-colors"
          data-testid={`${testBase}-reviews-toggle`}>
          <MessageSquareText size={13} className="text-[hsl(var(--cx-navy))]" />
          Latest Google Reviews ({list.length})
          {negatives > 0 && (
            <Badge className="text-[0.58rem] px-1.5 py-0 h-4 bg-[hsl(var(--cx-crimson))] text-white border-transparent hover:bg-[hsl(var(--cx-crimson))]"
              data-testid={`${testBase}-reviews-negative-count`}>
              {negatives} negative
            </Badge>
          )}
          <ChevronDown size={14} className={cn("ml-auto text-muted-foreground transition-transform duration-200", open && "rotate-180")} />
        </button>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="space-y-1.5 mt-2" data-testid={`${testBase}-reviews-list`}>
          {list.map((r, i) => (
            <ReviewItem key={r.id || `${testBase}-rv-${i}`} review={r} testId={`${testBase}-review-${i}`} />
          ))}
        </div>
        <div className="flex items-center justify-between gap-2 mt-2">
          {googleUrl && (
            <a href={googleUrl} target="_blank" rel="noreferrer"
              className="flex items-center gap-1 text-[0.65rem] text-[hsl(var(--cx-navy))] underline-offset-2 hover:underline"
              data-testid={`${testBase}-reviews-google-link`}>
              View all on Google <ExternalLink size={10} />
            </a>
          )}
          <span className="text-[0.6rem] text-muted-foreground">Google shares ~5 recent reviews per fetch</span>
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
};
